/**
 * The image picker, as a collection browser (MK-4a).
 *
 * This is a RE-IMPLEMENTATION BEHIND AN UNCHANGED FAÇADE. Every clause of the
 * resolution contract below is exercised by product callers, and all of them
 * predate this rebuild:
 *
 *  · The promise resolves `undefined` on pick AND on cancel, and NEVER rejects.
 *    `ui/section-list.js` re-enables its Replace button in a `finally`; a
 *    cancel that did not settle strands that button forever.
 *  · `onSelect` receives exactly ONE of `{file, source, author?, authorUrl?}`
 *    or `{url, source, …}` — never both. The url form is the CORS fallback and
 *    skips the `pb-asset://` pipeline entirely.
 *  · A THROWING `onSelect` leaves the dialog open and does not settle.
 *  · `settleOnce(force)`: a soft close during an in-flight pick is ignored;
 *    only `force` wins. Opening a second picker force-settles the first.
 *
 * What the rebuild changes is everything else: the hand-built tab strip,
 * toolbar, grid, card, load-more button and variant popover are gone, and the
 * kit owns the list, the search, the chips, the paging, the two-view stage and
 * the end-of-list disclosure.
 */

import { openCollectionBrowser } from '../../ui/collection-browser.js';
import { THUMB_FAILED_ATTR } from '../../shared/thumbnail-url.js';
import { createButton, createSegmentedControl } from '../../ui/components.js';
import { landed, prefersReducedMotion } from '../../ui/feedback.js';
import { reportError } from '../../core/report-error.js';
import { localImages } from '../adapters/local-images.js';
import {
  getCachedKitView,
  isKitFolder,
  kitFolderSlug,
  resetKitAdapterState,
  setKitAssetPurpose
} from '../adapters/logonuri.js';
import {
  decorateKitFolders,
  folderMetaLine,
  paintKitHeader,
  qrCardMeta,
  qrCardTitleAttr
} from './kit-header.js';
import { setSiteImageInventory, resetSiteImageInventory } from '../adapters/site-images.js';
import { store } from '../../core/state.js';
import { getStoredValue, setStoredValue } from '../adapters/api-key-storage.js';
import {
  UPLOAD_TAB_KEY,
  LAST_TAB_STORAGE_KEY,
  LAST_QUERY_STORAGE_KEY,
  categoryKeyFor,
  visibleSources,
  visibleScopes,
  scopeOfSource,
  sourcesInScope,
  normalizeTabKey,
  hasCategories,
  categoriesFor,
  normalizeCategories
} from './sources.js';
import { prepareUploadFile, installDropTarget, createFileInput } from './upload.js';
import { toCollectionItem } from './card-model.js';
import { renderImageDetail } from './detail-view.js';

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 400;

/** The one live session, so a second open can force-settle the first. */
let activeSession = null;

/**
 * @param {Function} onSelect — receives `{file|url, source, author?, authorUrl?}`
 *   plus, when `slots` was given, the `slot` this pick is for. Never both
 *   `file` and `url`.
 * @param {{currentImageUrl?: string, context?: string, purpose?: 'app-icon',
 *   initialTab?: string,
 *   initialCategory?: string,
 *   slots?: Array<{id: string, label: string, ratio?: string, thumb?: string,
 *     filled?: boolean, data?: object}>}} [options]
 *   `slots` is the QUEUE: two or more targets to fill in one visit. The dialog
 *   stays open until every slot is filled, advancing to the next EMPTY one
 *   after each pick, and the caller routes each pick by the `slot` it is
 *   handed. Fewer than two slots is the same as none — the single-target
 *   behaviour and its resolution contract are byte-identical.
 * @returns {Promise<{status: 'picked'|'cancelled'}>}
 */
export function showImagePicker(onSelect, options = {}) {
  return new Promise((resolve) => {
    closeImagePicker({ force: true });

    let resolved = false;
    let anyPicked = false;
    let isPicking = false;
    let browser = null;
    let teardownDrop = null;
    let fileInput = null;
    let chrome = null;

    function settleOnce(force = false) {
      if (resolved) return;
      if (!force && isPicking) return;
      resolved = true;
      activeSession = null;
      teardownDrop?.();
      teardownDrop = null;
      fileInput?.destroy();
      fileInput = null;
      // The brand-kit adapter is a module singleton, so a narrowed purpose must
      // not outlive the dialog that asked for it.
      resetKitAdapterState();
      // Same singleton hazard: one project's photographs must not be browsable
      // from the next project's picker.
      resetSiteImageInventory();
      const close = browser?.close;
      browser = null;
      // `close()` is a REQUEST: the dialog owns the animation and calls back
      // into destroy(). Guarded because onClose is one of the paths that gets
      // here, and re-entering it would recurse.
      try {
        close?.();
      } catch (err) {
        reportError('image-picker', err, { user: 'silent' });
      }
      resolve({ status: anyPicked ? 'picked' : 'cancelled' });
    }

    activeSession = {
      settle(force = false) {
        settleOnce(force);
      }
    };

    setKitAssetPurpose(options.purpose || null);
    // SU-7. The "From this site" source is per-PROJECT, so its inventory is
    // filled here, before `visibleSources()` decides whether to offer it at
    // all. Read from the composition context rather than passed in by each
    // caller: `replaceImage`, `swapSvgToImage` and `replaceLogo` would each
    // have to remember, and a forgotten one is a tab that silently vanishes.
    setSiteImageInventory(store.getState()?.compositionContext?.clientProfile?.siteImages || []);
    const currentImageUrl = String(options.currentImageUrl || '');
    let currentLocalUrl = localImages.normalizeUrl(currentImageUrl);

    // ── source state ──────────────────────────────────────────────────────
    const sources = visibleSources();
    const requested = normalizeTabKey(
      options.initialTab || getStoredValue(LAST_TAB_STORAGE_KEY) || ''
    );
    // MK-4a: Upload is a TILE now, not a source. A stored "upload" tab, or a
    // named source this deployment does not show, falls back to the first
    // visible source rather than opening nothing.
    let activeKey =
      requested !== UPLOAD_TAB_KEY && sources.some((s) => s.key === requested)
        ? requested
        : sources[0]?.key || 'local';
    let activeEntry = sources.find((s) => s.key === activeKey) || null;
    let activeAdapter = activeEntry?.adapter || null;
    const scopes = visibleScopes();
    let activeScope = scopeOfSource(activeKey) || scopes[0]?.key || 'library';

    let categoryOptions = [];
    const requestedInitialCategory = String(options.initialCategory || '').trim();
    let initialCategoryForKey = requestedInitialCategory ? activeKey : '';
    let activeCategory =
      requestedInitialCategory || getStoredValue(categoryKeyFor(activeKey)) || '';
    let categoriesLoadedFor = null;
    let searchGeneration = 0;
    let searchAbort = null;
    let currentPage = 1;
    let totalResults = 0;
    let hiddenDeadCount = 0;
    let includeDead = false;
    let searchAttribution = '';
    let searchNotes = [];
    let lastResults = [];
    let loadMoreBtn = null;

    const storedQuery = getStoredValue(LAST_QUERY_STORAGE_KEY) || '';

    // ── the pick path ─────────────────────────────────────────────────────
    async function applyPick(item, variant = null) {
      if (isPicking || resolved) return;
      const data = item?.data || {};
      if (data.dead) return; // not activatable — MK-3
      isPicking = true;
      browser?.setItemBusy?.(item.id, true);
      const adapter = data.adapter || activeAdapter;
      const result = data.result;
      // Captured BEFORE the await. `queue.advance()` moves the cursor on
      // success, and a slow download plus an impatient chip click could
      // otherwise apply this pick to whichever slot the user landed on while
      // it was in flight.
      const slot = queue.current();
      try {
        const file = await adapter.download(variant ? withVariant(result, variant) : result);
        await onSelect({
          file,
          source: adapter.name,
          author: result.author,
          authorUrl: result.authorUrl,
          ...(slot ? { slot } : {})
        });
        markAccepted(browser?.itemEl?.(item.id));
        isPicking = false;
        completePick(slot, result?.thumb || '');
        return;
      } catch (err) {
        // The CORS fallback: some hosts block browser fetch() even though
        // <img> renders fine. Applying the RAW url skips the pb-asset://
        // pipeline entirely, so it is offered only by an adapter that says it
        // has one. Exactly one of {file} / {url} ever reaches onSelect.
        if (err?.code === 'CORS' && typeof adapter.getSourceUrl === 'function') {
          const sourceUrl = adapter.getSourceUrl(result);
          if (sourceUrl) {
            try {
              await onSelect({
                url: sourceUrl,
                source: `${adapter.name} (URL)`,
                author: result.author,
                authorUrl: result.authorUrl,
                ...(slot ? { slot } : {})
              });
              isPicking = false;
              completePick(slot, sourceUrl);
              return;
            } catch {
              // Fall through to the card error path.
            }
          }
        }
        // Failure → stay open, say so on the card, do NOT resolve.
        isPicking = false;
        browser?.setItemBusy?.(item.id, false);
        showItemError(item, err);
        if (!(err?.code === 'CORS' || err?.code === 'INVALID_URL' || err?.name === 'AbortError')) {
          reportError('image-picker', err, { user: 'silent' });
        }
      }
    }

    /**
     * One pick has landed. Fill its slot, advance, and leave only when the
     * queue is drained.
     *
     * THIS IS THE ONE CHANGE TO THE RESOLUTION CONTRACT at the top of the file,
     * and it is deliberately shaped to be a no-op for every caller that does
     * not pass `slots`: with no queue, `markFilled` reports "all filled" on the
     * first pick and the dialog settles exactly as it always did. A queue of
     * one behaves identically for the same reason.
     */
    function completePick(slot, thumbUrl) {
      anyPicked = true;
      queue.markFilled(slot, thumbUrl);
      if (queue.isDrained()) {
        settleOnce();
        return;
      }
      // More to fill. The grid stays exactly where it is — the search that
      // found this photo probably finds the next one too — and only the bar
      // moves on.
      queue.advance();
      paintQueue();
      // Through `announce`, NOT straight to `setStatus`: the repeat guard is
      // what stops a later repaint saying the same sentence twice, and writing
      // past it would leave the guard believing the region still held the
      // result count it last wrote.
      announce(`${slot?.label || 'Image'} updated. ${queue.remainingLabel()}`);
    }

    /**
     * The slot queue.
     *
     * DS-1: PROMOTED. The engine is `ui/slot-queue.js` and the chrome is the
     * kit's `queue` option; this surface is consumer 1 and the reason both
     * exist. What used to be 58 lines of `createQueue` plus a hand-built strip,
     * a delegated click handler and a `paintQueue` is now the option below.
     *
     * `slots` absent ⇒ `entries` is empty and every method degrades to the
     * single-target behaviour: `current()` is null (so `onSelect` sees no
     * `slot` key at all, exactly as before), and `isDrained()` is true the
     * moment anything is filled. That hinge did not move.
     *
     * THE INSTANCE IS THE BROWSER'S, read back from `browser.queue` below —
     * never a second one built here. Two instances would be two cursors, and
     * they would diverge the first time a chip was clicked: the strip would
     * paint from one and the pick would fill the slot named by the other. It is
     * null until the dialog opens, and every function that reads it runs after
     * that (a null `browser` short-circuits the whole session before then).
     */
    let queue = null;

    /**
     * The first beat of the dismissal (MK-6).
     *
     * `pb-coll-accepted` presses the card 4%; `landed()` is the repo's ONE
     * landed animation, reused rather than a second one — the attention
     * keyframe ledger is shrink-only and stands at 14.
     *
     * It is fired and NOT awaited, deliberately. `settleOnce` runs on the next
     * line and starts the kit's exit animation, so the press plays DURING the
     * dismissal rather than before it: cause and effect read as one movement,
     * and the user's result is not delayed by a frame of decoration. Delaying
     * the settle would also have put the resolution contract's timing under a
     * motion decision, which is the wrong thing to couple.
     */
    function markAccepted(el) {
      if (!el || prefersReducedMotion()) return;
      el.classList.add('pb-coll-accepted');
      landed(el);
    }

    function withVariant(result, variant) {
      if (!variant || !result?._raw) return result;
      // The adapter downloads `getSourceUrl(item)`, which reads `_raw`. A
      // chosen variant replaces the canonical url for THIS pick only — the
      // record in the list is untouched.
      // ...and it must carry THAT variant's vendored key, not the canonical's.
      // Propagating only the url silently reverts an explicitly chosen size to
      // hotlinking while every other pick resolves locally.
      return {
        ...result,
        _raw: {
          ...result._raw,
          _normalizedUrl: variant.url,
          url: variant.url,
          _vendoredKey: variant.vendoredKey || ''
        }
      };
    }

    function showItemError(item, err) {
      let msg;
      if (err?.code === 'SOURCE_OFFLINE') msg = 'Source offline. Pick another image.';
      else if (err?.code === 'CORS') msg = 'CORS blocked. Try stock photos.';
      else if (err?.code === 'INVALID_URL') msg = 'Invalid image URL. Try another.';
      else if (err?.name === 'AbortError' || err?.name === 'TimeoutError')
        msg = 'Timed out. Click to retry.';
      else msg = 'Failed. Click to retry.';
      browser?.updateItem?.(item.id, { meta: msg });
      const el = browser?.itemEl?.(item.id);
      if (el) {
        el.classList.add('pb-image-picker-item-error');
        el.title = msg;
      }
    }

    // ── search ────────────────────────────────────────────────────────────
    function abortSearch() {
      if (searchAbort) {
        searchAbort.abort();
        searchAbort = null;
      }
    }

    function searchOptsFor() {
      const opts = {};
      if (hasCategories(activeEntry)) opts.category = activeCategory;
      // MK-3: `keepUrl` exempts the image being replaced from the dead-source
      // exclusion. Without it a user whose current image has an offline source
      // opens the picker and finds it simply absent.
      if (currentLocalUrl) opts.keepUrl = currentLocalUrl;
      if (includeDead) opts.includeDead = true;
      return Object.keys(opts).length ? opts : undefined;
    }

    async function runSearch(query, page = 1) {
      if (resolved || !activeAdapter) return;
      abortSearch();
      const trimmed = String(query || '').trim();
      if (!activeAdapter.browseWithoutQuery && trimmed.length < MIN_QUERY_LENGTH) {
        lastResults = [];
        totalResults = 0;
        hiddenDeadCount = 0;
        currentPage = 1;
        browser?.setRecords([]);
        paintChrome();
        return;
      }
      const generation = ++searchGeneration;
      const controller = new AbortController();
      searchAbort = controller;
      currentPage = page;
      try {
        const data = await activeAdapter.search(trimmed, page, controller.signal, searchOptsFor());
        if (generation !== searchGeneration || resolved) return;
        searchAbort = null;
        totalResults = Number(data.total) || 0;
        hiddenDeadCount = Number(data.hiddenDeadCount) || 0;
        // MK-5: an adapter that fanned out answers with the attribution for
        // the providers it ACTUALLY queried, and with a note for any that
        // failed. One provider being rate limited must not blank the grid, and
        // must not silently drop that provider's attribution either.
        searchAttribution = String(data.attribution || '');
        searchNotes = Array.isArray(data.notes) ? data.notes : [];
        const incoming = (data.results || []).map(stampSourceUrl);
        lastResults = page === 1 ? incoming : lastResults.concat(incoming);
        browser?.setRecords(lastResults);
        paintChrome();
      } catch (err) {
        if (generation !== searchGeneration || resolved) return;
        searchAbort = null;
        if (err?.name === 'AbortError') return;
        handleSearchError(err);
      }
    }

    /** The canonical url, resolved ONCE so the card can mark "current". */
    function stampSourceUrl(result) {
      if (typeof activeAdapter?.getSourceUrl !== 'function') return result;
      try {
        return { ...result, _sourceUrl: activeAdapter.getSourceUrl(result) || '' };
      } catch {
        return result;
      }
    }

    function handleSearchError(err) {
      // An adapter with its own gate answers first: "you are not connected" and
      // "your plan does not include this" are neither an API key nor a network
      // failure, and both end in a button rather than a sentence.
      const gate = activeAdapter?.gateFor?.(err);
      if (gate) {
        browser?.setRecords([]);
        showGate({
          title: gate.title,
          help: gate.help,
          actionLabel: gate.actionLabel,
          onAction: async () => {
            if (gate.action === 'upgrade') {
              window.open(gate.upgradeUrl || 'https://logonuri.com/#pricing', '_blank', 'noopener');
              return;
            }
            const { ensureLogoNuriLink } = await import('../../ui/logonuri/connect-modal.js');
            const linked = await ensureLogoNuriLink({ reason: 'Import a brand kit' });
            if (!linked) return;
            hideGate();
            categoriesLoadedFor = null;
            resetKitAdapterState();
            if (options.purpose) setKitAssetPurpose(options.purpose);
            loadCategories();
            runSearch(browser?.getQuery?.() || '', 1);
          }
        });
        return;
      }
      const described = activeAdapter?.describeError?.(err);
      if (described) {
        setStatus(described);
        return;
      }
      if (err?.code === 'AUTH') {
        setStatus('Invalid API key. Update it in Settings → Integrations.');
        showKeyGate();
      } else if (err?.code === 'RATE') {
        setStatus('Rate limited. Try again in a moment.');
      } else if (err?.code === 'NO_KEY') {
        setStatus('');
        showKeyGate();
      } else {
        setStatus(
          `Could not reach ${activeAdapter?.name || 'this source'}. Check your connection.`
        );
      }
    }

    // ── the foreign chrome (tabs, gate, status, attribution) ──────────────
    //
    // Built against `api.body` in onOpen, exactly as the SVG library does: the
    // kit owns the shell, and a surface that needs a strip above the search
    // inserts it into that shell rather than forking the shell.
    function buildChrome(api) {
      // MK-4b: five source TABS became three scope SEGMENTS. The primitive is
      // MK-1a's `createSegmentedControl`, so this surface inherits the only
      // accessible vocabulary the repo has for an exclusive choice —
      // role=radiogroup, aria-checked, a roving tabindex, and arrows that
      // SELECT rather than merely focus.
      //
      // `createSegmentedControl` throws below two options, which is correct
      // and cannot fire here: Library and Stock are never withheld, and only
      // Brand removes itself.
      const segmented = createSegmentedControl({
        options: scopes.map((scope) => ({ value: scope.key, label: scope.label })),
        ariaLabel: 'Image source',
        value: activeScope,
        className: 'pb-image-picker-scopes',
        onChange: (scope) => switchScope(scope)
      });
      const tabs = document.createElement('div');
      // BOTH families. `pb-picker-sources` is the shared placement rule (the
      // head of the chip row, with the divider after it) that the SVG library
      // also carries; `pb-image-picker-sources` is kept because the geometry
      // harness captures it by name and a renamed selector is an unmeasured one.
      tabs.className = 'pb-picker-sources pb-image-picker-sources';
      tabs.appendChild(segmented.el);
      // MK-4b's provider control is GONE: MK-5 merged the three stock adapters
      // into one, so every scope has exactly one source and there is nothing
      // left to choose between. `sourcesInScope` still returns a list, and a
      // future scope with two members would need it back.

      const gate = document.createElement('div');
      gate.className = 'pb-image-picker-key-prompt';
      gate.hidden = true;
      const message = document.createElement('div');
      message.className = 'pb-image-picker-key-message';
      const gateTitle = document.createElement('strong');
      gateTitle.className = 'pb-image-picker-key-title';
      const gateHelp = document.createElement('small');
      gateHelp.className = 'pb-image-picker-key-help';
      message.append(gateTitle, gateHelp);
      // Through the kit, never a hand-rolled <button> — QI-5's
      // no-hand-rolled-button rule is why this file leaves the sweep allowlist.
      const gateBtn = createButton({
        variant: 'primary',
        size: 'sm',
        label: 'Open Settings',
        className: 'pb-image-picker-key-settings',
        onClick: () => gateAction?.()
      });
      gate.append(message, gateBtn);

      const status = document.createElement('p');
      status.className = 'pb-image-picker-status';

      // The footer band: what the list holds on the left, who it came from on
      // the right. The count was previously announced to the live region and
      // painted NOWHERE, so a sighted user could not see what a screen reader
      // was being told — one derivation, two consumers, is the fix.
      const foot = document.createElement('div');
      foot.className = 'pb-image-picker-foot';
      const count = document.createElement('span');
      count.className = 'pb-image-picker-count';
      const attribution = document.createElement('small');
      attribution.className = 'pb-image-picker-attribution';
      foot.append(count, attribution);

      const shell = api.listEl.closest('.pb-coll-shell');
      const scroller = api.listEl.closest('.pb-coll-scroll');
      const chipRow = shell?.querySelector('.pb-coll-chips');

      // ── ONE BAR ─────────────────────────────────────────────────────────
      //
      // DS-1: the bar is the KIT's now. `queue: {...}` on the open call below
      // builds the strip, delegates the chip click, wires the arrow keys the
      // hand-rolled version never had, and performs this exact header takeover
      // — the `.pb-coll-bar` class, the sr-only heading, the queue first and
      // the search moved in before the close button. Nothing about the shape
      // changed; it just stopped being this file's.
      //
      // What is left here is the picker's own chrome, which the kit does not
      // and should not own: the scope segmented control, the API-key gate, the
      // status line and the footer band.

      // The scope control lands at the head of the CHIP row — source and filter
      // are both "narrow what the grid shows", and the bar above is about the
      // TARGET. It gets there through the kit's `chips.leading` hook, NOT by
      // being prepended here: `renderChipRow` starts with `textContent = ''`,
      // so a hand-prepended control survives exactly until the first chip
      // click. `chipRow` is only the fallback host for a chip-less surface.
      if (!chipRow) shell?.insertBefore(tabs, shell.firstChild);

      if (scroller) {
        shell?.insertBefore(gate, scroller);
        scroller.after(status);
        status.after(foot);
      }
      return {
        tabs,
        segmented,
        gate,
        gateTitle,
        gateHelp,
        gateBtn,
        status,
        scroller,
        foot,
        count,
        attribution
      };
    }

    /**
     * The bar's left half. DS-1: the chips, the roving tabindex and the paint
     * are the kit's; what stays here is the two SENTENCES, which are this
     * surface's and always were.
     */
    function paintQueue() {
      browser?.paintQueue?.();
    }

    /** The bar's title: the slot's label, or the dialog's name at zero slots. */
    function queueTitleFor(slot) {
      return slot?.label || 'Browse images';
    }

    /** The bar's sub line. */
    function queueSubFor(slot, { cursor, size, filledCount }) {
      if (size >= 2) {
        const parts = [`${cursor + 1} of ${size}`];
        if (slot?.ratio) parts.push(slot.ratio);
        parts.push(`${filledCount} filled`);
        return parts.join(' · ');
      }
      // One slot has no position to report and nothing to count down, so the
      // sub falls back to what the caller already said about the target — the
      // PP-0 context sentence, which is otherwise homeless now that the header
      // is a bar.
      return slot?.ratio || String(options.context || '');
    }

    let gateAction = null;

    function showGate({ title, help, actionLabel, onAction }) {
      if (!chrome) return;
      chrome.gate.hidden = false;
      chrome.gateTitle.textContent = title;
      chrome.gateHelp.textContent = help;
      const label = chrome.gateBtn.querySelector('.pb-btn-label') || chrome.gateBtn;
      label.textContent = actionLabel;
      gateAction = onAction;
      if (browser?.searchInput) browser.searchInput.disabled = true;
      chrome.gateBtn.focus();
    }

    function hideGate() {
      if (!chrome) return;
      chrome.gate.hidden = true;
      gateAction = null;
      if (browser?.searchInput) browser.searchInput.disabled = false;
    }

    function showKeyGate() {
      if (!activeAdapter) return;
      showGate({
        title: `${activeAdapter.name} API key needed`,
        help: activeAdapter.keyHelp || 'Add this key in Project settings.',
        actionLabel: 'Open Settings',
        onAction: async () => {
          settleOnce(true);
          try {
            const { openSettingsModal } = await import('../../ui/settings-modal.js');
            openSettingsModal({ category: 'integrations' });
          } catch (err) {
            reportError('image-picker', err, {
              user: 'toast',
              message: 'Could not open integration settings.'
            });
          }
        }
      });
    }

    function setStatus(text) {
      if (chrome) chrome.status.textContent = String(text || '');
    }

    /**
     * MK-7: ONE announcement per state change, not one per keystroke.
     *
     * The search is debounced 400ms and every path funnels through
     * `paintChrome`, so a live-region write happens once the results have
     * settled rather than on each character. The repeat guard is the other
     * half: `aria-live` re-announces on any write, so re-writing the same
     * sentence — which a chip repaint does — would say "12 images" twice.
     */
    let lastAnnouncement = '';
    function announce(text) {
      const next = String(text || '');
      if (next === lastAnnouncement) return;
      lastAnnouncement = next;
      browser?.setStatus?.(next);
    }

    /** What the footer says and the live region announces — ONE derivation. */
    function countLabel() {
      const loaded = lastResults.length;
      const noun = loaded === 1 ? 'image' : 'images';
      // `!includeDead` matters: once the disclosure has been used those records
      // are IN the list, and still calling them hidden would be false.
      return hiddenDeadCount > 0 && !includeDead
        ? `${loaded} ${noun}, ${hiddenDeadCount} unavailable and hidden`
        : `${loaded} ${noun}`;
    }

    /**
     * The brand-kit surfaces: the header card for an open kit, and the palette
     * strips on folder rows.
     *
     * Called from `paintChrome` rather than from a second `onRender` — the kit
     * gives a surface ONE render hook and this one is already spoken for.
     * Both halves are no-ops on every other source, so there is no branch at
     * the call site.
     */
    function paintBrandKitChrome() {
      if (!browser) return;
      if (activeKey !== 'logonuri') {
        paintKitHeader({ scroller: chrome.scroller, listEl: browser.listEl, kit: null });
        return;
      }
      decorateKitFolders(browser);
      // Cache-only, so a cold cache paints nothing this frame and the render
      // triggered by the search that fills it paints the card.
      const kit = activeCategory ? getCachedKitView(activeCategory) : null;
      paintKitHeader({
        scroller: chrome.scroller,
        listEl: browser.listEl,
        kit,
        purpose: options.purpose || '',
        onApply: applyWholeKit
      });
    }

    /**
     * Apply the whole kit, from the header card's primary button.
     *
     * ORDER IS THE POINT. `applyBrandKit` opens by closing anything that would
     * swallow its history entry and commits inside one `store.beginTransaction`
     * — so it must not run underneath an open modal with a caller awaiting a
     * pick. The picker settles FIRST, resolving that caller as `cancelled`,
     * which is the truth: no image was chosen. The kit apply then owns its own
     * confirm dialog, its own undo toast and its own failure reporting.
     *
     * The flow itself — confirm copy, the four outcome sentences, error
     * reporting — is `logonuri/apply-kit-flow.js`, shared with the wizard's
     * import path. Imported dynamically so the picker keeps no static edge to
     * the apply engine: the deploy bundle preloads the static graph, and the
     * apply pulls in the whole planner, the recolour pass and asset adoption
     * for a button most sessions never press.
     */
    async function applyWholeKit(kit) {
      if (!kit?.slug) return;
      settleOnce(true);
      const { runBrandKitApply } = await import('../../ui/logonuri/apply-kit-flow.js');
      await runBrandKitApply(kit, { errorScope: 'image-picker-brand-kit' });
    }

    /** The status line, the Load More button and the footer band, together. */
    function paintChrome() {
      if (!chrome || resolved) return;
      paintBrandKitChrome();
      chrome.attribution.textContent = searchAttribution || activeAdapter?.attribution || '';

      const loaded = lastResults.length;
      if (loadMoreBtn) {
        loadMoreBtn.remove();
        loadMoreBtn = null;
      }
      if (loaded > 0 && loaded < totalResults) {
        loadMoreBtn = createButton({
          variant: 'ghost',
          className: 'pb-image-picker-more',
          label: `Load more (${loaded} of ${totalResults})`,
          onClick: () => runSearch(browser?.getQuery?.() || '', currentPage + 1)
        });
        // Inside the SCROLLER, at the end of the list it extends — not above
        // the footer, where it would sit in fixed chrome and read as a
        // permanent control rather than as the end of what has loaded.
        chrome.scroller?.appendChild(loadMoreBtn);
      }

      chrome.count.textContent = loaded > 0 ? countLabel() : '';
      // An empty band is a grey strip with a border and nothing in it. Both
      // halves gone ⇒ no band. (The `[hidden]` guard in media-kit.css is not
      // optional: `display: flex` on the class would otherwise beat the UA's
      // `[hidden]` rule — which is the exact defect this same dialog had with
      // its API-key gate.)
      chrome.foot.hidden = !chrome.count.textContent && !chrome.attribution.textContent;

      if (loaded > 0) {
        // A partial fan-out failure is stated QUIETLY, beside the results the
        // other providers returned — never as the error state.
        setStatus(searchNotes.join(' · '));
        announce(countLabel());
        return;
      }
      const query = String(browser?.getQuery?.() || '').trim();
      if (activeAdapter?.needsKey && !activeAdapter.getApiKey()) {
        setStatus('');
        announce(`${activeAdapter.name} needs an API key`);
      } else if (!activeAdapter?.browseWithoutQuery && query.length < MIN_QUERY_LENGTH) {
        setStatus('Type at least 2 characters to search.');
        announce('Type at least 2 characters to search');
      } else {
        setStatus('');
        announce(query ? `No images match ${query}` : 'No images');
      }
    }

    // ── scopes, sources + categories ──────────────────────────────────────
    /**
     * A scope is a KIND of source. MK-4b needed this to remember which
     * PROVIDER a scope was last on; MK-5 merged the three stock adapters into
     * one, so every scope has exactly one source and the memory has nothing to
     * remember. The indirection stays because the scope model still describes
     * a list, and collapsing it would have to be undone by the first scope
     * that grows a second source.
     */
    function switchScope(scopeKey) {
      const inScope = sourcesInScope(scopeKey);
      if (!inScope.length || scopeKey === activeScope) return;
      activeScope = scopeKey;
      switchSource(inScope[0]);
    }

    function switchSource(key) {
      const entry = sources.find((s) => s.key === key);
      if (!entry) return;
      abortSearch();
      activeKey = key;
      activeEntry = entry;
      activeAdapter = entry.adapter;
      activeScope = scopeOfSource(key) || activeScope;
      initialCategoryForKey = '';
      activeCategory = getStoredValue(categoryKeyFor(key)) || '';
      categoryOptions = [];
      includeDead = false;
      lastResults = [];
      totalResults = 0;
      hiddenDeadCount = 0;
      searchAttribution = '';
      searchNotes = [];
      setStoredValue(LAST_TAB_STORAGE_KEY, key);
      chrome?.segmented?.set(activeScope);
      browser?.setRecords([]);
      browser?.setChip?.(activeCategory || 'all');
      hideGate();
      if (activeAdapter.needsKey && !activeAdapter.getApiKey()) {
        showKeyGate();
        paintChrome();
        return;
      }
      loadCategories();
      runSearch(browser?.getQuery?.() || '', 1);
      paintChrome();
      browser?.searchInput?.focus();
    }

    async function loadCategories() {
      if (!hasCategories(activeEntry)) {
        categoryOptions = [];
        browser?.setRecords(lastResults);
        return;
      }
      const forKey = activeKey;
      if (categoriesLoadedFor === forKey) return;
      categoriesLoadedFor = forKey;
      try {
        const cats = await categoriesFor(activeEntry);
        if (activeKey !== forKey || resolved) return;
        categoryOptions = normalizeCategories(cats);
        const stored = getStoredValue(categoryKeyFor(forKey)) || '';
        const wanted =
          initialCategoryForKey === forKey && requestedInitialCategory
            ? requestedInitialCategory
            : stored;
        initialCategoryForKey = '';
        activeCategory = wanted && categoryOptions.some((c) => c.value === wanted) ? wanted : '';
        // Repaint the chip row against the new key set.
        browser?.setRecords(lastResults);
        if (activeCategory) browser?.setChip?.(activeCategory);
        runSearch(browser?.getQuery?.() || '', 1);
      } catch (err) {
        // A category list that will not load is not fatal — the grid still
        // searches. A LINK failure, though, must reach the gate: silently
        // showing an empty kit list would look like "you have no kits".
        if (activeKey === forKey && activeAdapter?.gateFor?.(err)) handleSearchError(err);
      }
    }

    // ── upload ────────────────────────────────────────────────────────────
    async function acceptUpload(file) {
      if (isPicking || resolved) return;
      isPicking = true;
      const slot = queue.current();
      try {
        const prepared = await prepareUploadFile(file);
        await onSelect({ file: prepared, source: 'Upload', ...(slot ? { slot } : {}) });
        // The upload TILE is what accepted the file, so it is what confirms it.
        markAccepted(browser?.listEl?.querySelector('[data-cb-tile="upload"]'));
        isPicking = false;
        // The same completion path as a grid pick: an upload fills a slot and
        // advances the queue too. Routing it separately is how "it works when
        // you pick but not when you drop" bugs happen.
        completePick(slot, '');
      } catch (err) {
        isPicking = false;
        setStatus(err?.message || 'Upload failed. Try again.');
      }
    }

    // ── open ──────────────────────────────────────────────────────────────
    browser = openCollectionBrowser({
      // Kept for the dialog's ACCESSIBLE NAME (`aria-labelledby` points at the
      // <h2>); `buildChrome` then hides it visually. PP-0's "a picker states
      // its target" is not weakened by that — it is strengthened: the bar names
      // the exact slot, its position in the queue and how far through you are,
      // where the context line said "Replacing an image in Hero" and stopped.
      // The sentence itself survives as the single-slot sub line.
      title: `Browse ${String(options.noun || 'Image').toLowerCase()}s`,
      testid: 'image-picker',
      // TWO families, the same split the SVG library carries: `pb-picker-surface`
      // is the SHARED band system (dialog height, zeroed body padding, the
      // chip/scroll/gate gutters, the tile dressing, the drop outline), and
      // `pb-image-picker-modal` is what is genuinely this dialog's alone —
      // liveness dressing, the accepted flash, the phone gutter.
      className: 'pb-image-picker-modal pb-picker-surface',
      // 1040, the prototype's width. At 800 the media track resolved four
      // columns and the dialog was narrower than the add-section browser it
      // sits beside; a picker whose job is "show me what I have" is the one
      // surface in the kit that trades chrome for reach.
      maxWidth: 1040,
      layout: 'grid',
      // A grid of IMAGES, not of records — see --pb-coll-track-min-media.
      density: 'media',
      listTag: 'div',
      itemTag: 'div',
      listRole: 'listbox',
      listAriaLabel: 'Images',
      itemRole: 'option',
      headerMeta: false,
      initialFocus: 'search',
      // DS-1: the promoted slot queue. The kit owns the engine, the strip, the
      // header takeover and — new here — the arrow keys; this surface supplies
      // its slots and its two sentences. `slots` may be undefined, and that is
      // the ordinary case: the queue degrades to naming one target, which is
      // the shape the bar has had since MK-4.
      queue: {
        slots: options.slots,
        noun: String(options.noun || 'Image'),
        ariaLabel: `${String(options.noun || 'Image')} slots`,
        title: queueTitleFor,
        sub: queueSubFor
      },
      search: {
        placeholder: `Search ${String(options.noun || 'Image').toLowerCase()}s…`,
        ariaLabel: `Search ${String(options.noun || 'Image').toLowerCase()}s`,
        initialQuery: storedQuery,
        debounceMs: SEARCH_DEBOUNCE_MS,
        onQuery: (value) => {
          setStoredValue(LAST_QUERY_STORAGE_KEY, String(value || '').trim());
          runSearch(value, 1);
        }
      },
      chips: {
        ariaLabel: 'Filter by category',
        // The scope control, re-parked at the head of the row on every repaint.
        // Safe to read `chrome` lazily: `onOpen` runs before the first load,
        // and therefore before the first paint of this row.
        leading: () => chrome?.tabs || null,
        // Categories only. A stock source has none, so the row is just the
        // engine's `all` there — see the provider control above for why the
        // providers are NOT chips.
        //
        // `keysOf` over the RECORDS fixes a live bug: the old `<select>` was
        // built from `getCategories()`, which lists only `sectionCategory`, so
        // an image reachable only through its second merged category was
        // filterable but never listable.
        keysOf: (record) => {
          const raw = record?._raw || {};
          const keys = new Set();
          if (raw.sectionCategory) keys.add(String(raw.sectionCategory));
          for (const cat of raw._sectionCategories || []) keys.add(String(cat));
          return [...keys];
        },
        extra: () => categoryOptions.map((c) => c.value),
        labelOf: (key) =>
          key === 'all'
            ? 'All'
            : categoryOptions.find((c) => c.value === key)?.label ||
              key.charAt(0).toUpperCase() + key.slice(1),
        activeKey: activeCategory || 'all',
        onChange: (key) => {
          activeCategory = key === 'all' ? '' : key;
          setStoredValue(categoryKeyFor(activeKey), activeCategory);
          runSearch(browser?.getQuery?.() || '', 1);
        }
      },
      // The adapter has already filtered and paginated; everything fed here is
      // a match. The kit's own filter would re-filter the page and hide rows
      // the adapter meant to show.
      filter: () => true,
      skeleton: { variant: 'image', count: () => 8 },
      illustration: 'images',
      // BOTH point at one decider. `isFiltering` splits them on whether a
      // query/chip is active, but the three cases this picker has to tell
      // apart — an empty library, a search that matched nothing, and a set
      // whose every match is offline — do not line up with that split: the
      // all-offline case happens with no query at all.
      emptyCopy: () => emptyCopyFor(),
      filteredEmptyCopy: () => emptyCopyFor(),
      tiles: {
        leading: [
          {
            id: 'upload',
            kind: 'create',
            label: 'Upload an image',
            description: 'JPG, PNG, WebP, GIF, AVIF, SVG',
            onActivate: () => fileInput?.open()
          }
        ]
      },
      disclosure: {
        // MK-3 built the adapter half (`includeDead`); this is the control the
        // plan named. Visibility is DERIVED by the engine every paint, and it
        // is refused at zero visible records — at which point the empty state
        // owns the single reveal control.
        hidden: () => (includeDead ? 0 : hiddenDeadCount),
        label: (n) => (n === 1 ? 'Show 1 unavailable image' : `Show ${n} unavailable images`),
        onReveal: async () => {
          includeDead = true;
          await runSearch(browser?.getQuery?.() || '', 1);
        }
      },
      detail: {
        // Back is the engine's, on the dialog header. The commit control comes
        // from the render because its disabled state depends on the item.
        render: (item) =>
          renderImageDetail(item, {
            view: browser.detailView,
            onPick: (picked, variant) => applyPick(picked, variant),
            setActions: (els) => browser?.setDetailActions(els),
            basenameOf: (url) => localImages.basenameFromUrl(url)
          })
      },
      load: async () => {
        if (activeAdapter?.needsKey && !activeAdapter.getApiKey()) return [];
        await loadCategories();
        await runSearch(storedQuery, 1);
        return lastResults;
      },
      idOf: (record, i) => String(record?.id ?? i),
      adapt: (record) => {
        const item = toCollectionItem(record, {
          sourceKey: activeKey,
          sourceName: activeAdapter?.name || '',
          currentUrl: currentLocalUrl
        });
        // A brand-kit FOLDER is a record in the same grid as the images, but it
        // is not one: it has no dimensions (so `metaLineFor` renders an empty
        // string), no variants, and no preview worth opening — the thing behind
        // it is a list, not a bigger copy of the poster. Patched here rather
        // than branched inside `toCollectionItem`, which is the shared card
        // model and has no business knowing about LogoNuri.
        if (isKitFolder(record)) {
          item.meta = folderMetaLine(record._folder);
          item.titleAttr = `Open the ${item.title} brand kit`;
          // Contain, on the checkerboard: a wordmark cropped to a 3/2 poster is
          // the exact failure the asset-fit rule exists to prevent, and a kit's
          // mark is the one asset guaranteed not to be a photograph.
          item.poster = { ...item.poster, fit: 'contain', background: 'checker' };
          item.actions = [];
          item.data.adapter = activeAdapter;
          return item;
        }
        // The QR card's meta line is its MODE, not its dimensions (LN-10 slice
        // B). `metaLineFor` would render nothing — the record carries no
        // width/height — and the mode is the fact the card exists to disclose:
        // a dynamic code's destination stays editable in LogoNuri after the
        // export ships. Patched here for the same reason as the folder patch
        // above: `toCollectionItem` is the shared card model and has no
        // business knowing about LogoNuri.
        if (record?._publicKey === 'qr_code') {
          item.meta = qrCardMeta(record._qr);
          const scansTo = qrCardTitleAttr(record._qr);
          if (scansTo) item.titleAttr = scansTo;
        }
        // The "N sizes" badge became a REAL control: it opens the detail view,
        // which can act on the list it shows. A single-variant card keeps the
        // affordance as a plain preview — the detail is also where provenance
        // and the offline explanation live.
        const variants = record?.variants || record?._raw?._variants || [];
        item.actions = [
          {
            id: 'detail',
            label: variants.length > 1 ? `${variants.length} sizes` : 'Preview',
            tooltip: variants.length > 1 ? 'Choose a size' : 'Preview this image'
          }
        ];
        item.data.adapter = activeAdapter;
        return item;
      },
      activateOnItemClick: true,
      onActivate: (item) => {
        // THE PICK BOUNDARY. A folder is not an image: activating it must open
        // the kit, never resolve the caller's promise with a poster URL. Routed
        // through the chip the category filter already owns, so entering a kit
        // is the same state change as pressing its chip — one code path, and
        // the chip row stays truthful about where you are.
        const slug = kitFolderSlug(item?.data?.result);
        if (slug) {
          browser?.setChip(slug);
          return;
        }
        applyPick(item);
      },
      onAction: (action, item) => {
        if (action === 'detail') browser?.showDetail(item.id);
      },
      onOpen: (api) => {
        chrome = buildChrome(api);
        paintQueue();
        // `error` does not BUBBLE but it does CAPTURE, so one listener here
        // covers every poster image the kit renders, including pages added
        // later. Per-card wiring would miss those, and the kit's own
        // thumbnail pool only handles `thumbKey` posters, not `src` ones.
        api.listEl.addEventListener(
          'error',
          (event) => {
            // Duck-typed, not `instanceof HTMLImageElement`: the jsdom
            // harnesses these suites run in do not put that constructor on
            // globalThis, and a ReferenceError inside a capture listener is
            // swallowed by the event loop rather than failing loudly.
            const img = event.target;
            if (!img || img.tagName !== 'IMG') return;
            if (!img.closest?.('.pb-coll-poster')) return;
            img.setAttribute(THUMB_FAILED_ATTR, '1');
          },
          true
        );
        fileInput = createFileInput(acceptUpload);
        api.body.appendChild(fileInput.el);
        // MK-4a: the WHOLE dialog is the drop target. The upload tab was the
        // only place a drop landed, so dropping onto the grid — the obvious
        // gesture once the grid is what fills the dialog — did nothing.
        teardownDrop = installDropTarget(api.modal || api.body, acceptUpload);
        if (activeAdapter?.needsKey && !activeAdapter.getApiKey()) showKeyGate();
      },
      onRender: () => paintChrome(),
      onClose: () => settleOnce(true)
    });

    // The testid guard returns null when a picker is already open and NOT
    // closing. `closeImagePicker({force:true})` above marks the previous one
    // closing, so this is unreachable in practice — but an unsettled promise
    // is a stranded Replace button, so it fails loudly rather than silently.
    if (!browser) {
      resolved = true;
      activeSession = null;
      resetKitAdapterState();
      resolve({ status: 'cancelled' });
      return;
    }

    // DS-1: adopt the kit's queue. One instance, one cursor — see its
    // declaration above for why this is read back rather than built here.
    queue = browser.queue;

    if (currentImageUrl) {
      localImages
        .findByUrl(currentImageUrl)
        .then((match) => {
          if (resolved || !match) return;
          currentLocalUrl = localImages.getSourceUrl(match) || currentLocalUrl;
        })
        .catch(() => {
          /* a current image we cannot resolve is simply not highlighted */
        });
    }

    function emptyCopyFor() {
      const query = String(browser?.getQuery?.() || '').trim();
      if (hiddenDeadCount > 0 && !includeDead) {
        return {
          title:
            hiddenDeadCount === 1
              ? 'The one match has an offline source'
              : `All ${hiddenDeadCount} matches have offline sources`,
          body: 'Their images were never archived. Try another word or source.'
        };
      }
      if (!query) {
        return {
          title: 'Your image library is empty.',
          body: 'Upload an image to start your library, or search a stock source.'
        };
      }
      return {
        title: `No images match “${query}”`,
        body: 'Try a different word, or another source.'
      };
    }
  });
}

export function closeImagePicker({ force = false } = {}) {
  if (activeSession) activeSession.settle(force);
}
